/** Loading placeholders.
 *
 *  The rule this exists to enforce: **the app never leaves you guessing whether
 *  something is happening.** Every wait shows a placeholder shaped like the
 *  content that is coming, not a spinner floating in an empty screen. A
 *  skeleton says "a folder list is arriving and it will be about this big"; a
 *  spinner says only "something, somewhere, maybe".
 *
 *  One primitive rather than `animate-pulse bg-slate-200` scattered around,
 *  because those hardcoded greys ignore the active theme — a slate shimmer on
 *  Neon Cyan or Editorial Noir looks like a rendering fault. These tint from
 *  the page's own ink instead, so they belong to whatever theme is on.
 *
 *  `role="status"` with a screen-reader label on the group, not on every bar,
 *  or a reader announces "loading" a dozen times for one screen. */

export function Skeleton({
  className = "",
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={`block animate-pulse rounded bg-black/[0.07] dark:bg-white/[0.09] motion-reduce:animate-none ${className}`}
      style={style}
    />
  );
}

/** Wrapper that announces one wait, however many bars it contains. */
export function SkeletonGroup({
  label,
  className = "",
  style,
  children,
}: {
  label: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div role="status" aria-label={label} aria-live="polite" aria-busy="true" className={className} style={style}>
      {children}
    </div>
  );
}

/** A count badge that has not arrived yet. Sized like a real one so the row
 *  does not jump sideways when the number lands. */
export function SkeletonBadge(): JSX.Element {
  return <Skeleton className="h-[15px] w-6 shrink-0 rounded-full" />;
}

/** Placeholder for a whole routed page, used as the Suspense fallback while a
 *  lazily-loaded route downloads.
 *
 *  Deliberately generic — a title bar and some rows. It stands in for pages of
 *  different shapes, and pretending to know the layout of a page that has not
 *  loaded produces a worse flash when the real one arrives. */
export function PageSkeleton(): JSX.Element {
  return (
    <SkeletonGroup label="Loading" className="mx-auto w-full max-w-4xl space-y-6 p-6">
      <div className="space-y-3">
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="space-y-2.5">
        {[92, 78, 85, 64, 88, 71].map((w, i) => (
          <Skeleton key={i} className="h-12" style={{ width: `${w}%` }} />
        ))}
      </div>
    </SkeletonGroup>
  );
}
